import { FontAwesome6 } from '@expo/vector-icons';
import React from 'react';
import {
    Platform,
    StyleProp,
    StyleSheet,
    Text,
    TextStyle,
    TouchableOpacity,
    TouchableOpacityProps,
    View,
    ViewStyle,
} from 'react-native';
import colors from '../../lib/colors';
import { fonts } from '../../lib/fonts';
import { msg } from '@lingui/core/macro';
import {
    type LocalizedMessage,
    useResolvedMessage,
} from '../../lib/i18n/messages';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';

export interface ButtonProps extends TouchableOpacityProps {
    label: LocalizedMessage;
    variant?: ButtonVariant;
    href?: string;
    iconName?: React.ComponentProps<typeof FontAwesome6>['name'];
    iconSize?: number;
    iconColor?: string;
    leadingIcon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
    textStyle?: StyleProp<TextStyle>;
    contentStyle?: StyleProp<ViewStyle>;
}

const BUTTON_ICON_COLORS: Record<ButtonVariant, string> = {
    primary: '#ffffff',
    secondary: '#0B1020',
    ghost: '#0f172a',
};

function getContainerStyle(variant: ButtonVariant, disabled?: boolean) {
    if (variant === 'primary') {
        return disabled ? styles.btnPrimaryDisabled : styles.btnPrimary;
    }

    if (variant === 'secondary') {
        return styles.btnSecondary;
    }

    return styles.btnGhost;
}

function getTextStyle(variant: ButtonVariant) {
    if (variant === 'primary') {
        return styles.btnPrimaryText;
    }

    if (variant === 'secondary') {
        return styles.btnSecondaryText;
    }

    return styles.btnGhostText;
}

function getWebLinkStyle(style: TouchableOpacityProps['style'], variant: ButtonVariant, disabled?: boolean) {
    const linkStyle = StyleSheet.flatten([
        styles.btn,
        getContainerStyle(variant, disabled),
        style,
        { display: 'flex', textDecorationLine: 'none' },
    ]) as any;

    if (linkStyle.paddingHorizontal !== undefined) {
        linkStyle.paddingLeft = linkStyle.paddingHorizontal;
        linkStyle.paddingRight = linkStyle.paddingHorizontal;
        delete linkStyle.paddingHorizontal;
    }

    if (linkStyle.paddingVertical !== undefined) {
        linkStyle.paddingTop = linkStyle.paddingVertical;
        linkStyle.paddingBottom = linkStyle.paddingVertical;
        delete linkStyle.paddingVertical;
    }

    return linkStyle;
}

const Button = React.forwardRef<React.ElementRef<typeof TouchableOpacity>, ButtonProps>(function Button(
    {
        label,
        variant = 'primary',
        href,
        iconName,
        iconSize = 14,
        iconColor,
        leadingIcon,
        trailingIcon,
        textStyle,
        contentStyle,
        disabled,
        onPress,
        style,
        activeOpacity = 0.85,
        ...touchableProps
    },
    ref
) {
    const resolvedLabel = useResolvedMessage(label);
    const resolvedIconColor = iconColor || BUTTON_ICON_COLORS[variant];
    const content = (
        <View style={[styles.content, contentStyle]}>
            {leadingIcon || (iconName ? (
                <FontAwesome6
                    name={iconName}
                    size={iconSize}
                    color={resolvedIconColor}
                />
            ) : null)}
            <Text style={[getTextStyle(variant), textStyle]}>
                {resolvedLabel}
            </Text>
            {trailingIcon}
        </View>
    );

    if (href && Platform.OS === 'web') {
        return React.createElement(
            'a',
            {
                href,
                onClick: disabled ? (event: { preventDefault?: () => void }) => event.preventDefault?.() : onPress as any,
                ref: ref as any,
                style: getWebLinkStyle(style, variant, disabled),
            },
            content
        );
    }

    return (
        <TouchableOpacity
            ref={ref}
            disabled={disabled}
            activeOpacity={activeOpacity}
            onPress={onPress}
            style={[styles.btn, getContainerStyle(variant, disabled), style]}
            {...touchableProps}
        >
            {content}
        </TouchableOpacity>
    );
});

export default Button;

type ShortcutButtonProps = Omit<ButtonProps, 'iconName' | 'label'>;

export const PaperFeedButton = React.forwardRef<
    React.ElementRef<typeof TouchableOpacity>,
    ShortcutButtonProps
>(function PaperFeedButton(props, ref) {
    return (
        <Button
            ref={ref}
            label={msg({ id: 'button.paperFeed', message: 'Paper Feed' })}
            iconName="newspaper"
            {...props}
        />
    );
});

export const ProjectSettingsButton = React.forwardRef<
    React.ElementRef<typeof TouchableOpacity>,
    ShortcutButtonProps
>(function ProjectSettingsButton(props, ref) {
    return (
        <Button
            ref={ref}
            label={msg({ id: 'button.projectSettings', message: 'Project Settings' })}
            iconName="sliders"
            {...props}
        />
    );
});

const styles = StyleSheet.create({
    btn: {
        paddingHorizontal: 18,
        paddingVertical: 12,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },
    btnPrimary: {
        backgroundColor: colors.zillaGreen,
        ...(Platform.OS !== 'web' && {
            shadowColor: "#000",
            shadowOpacity: 0.12,
            shadowRadius: 8,
            shadowOffset: { width: 0, height: 4 },
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
        }),
    },
    btnPrimaryDisabled: {
        backgroundColor: '#b8dac9',
        ...(Platform.OS !== 'web' && {
            shadowColor: "#000",
            shadowOpacity: 0.06,
            shadowRadius: 4,
            shadowOffset: { width: 0, height: 2 },
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
        }),
    },
    btnSecondary: {
        backgroundColor: colors.card,
        borderWidth: 1,
        borderColor: colors.border,
        ...(Platform.OS !== 'web' && {
            shadowColor: "#000",
            shadowOpacity: 0.06,
            shadowRadius: 6,
            shadowOffset: { width: 0, height: 2 },
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
        }),
    },
    btnGhost: {
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: '#cbd5e1'
    },
    btnPrimaryText: {
        ...fonts.button,
        color: '#fff',
        fontWeight: '600',
        textAlign: 'center',
    },
    btnSecondaryText: {
        ...fonts.button,
        color: "#0B1020",
        fontWeight: '500',
        textAlign: 'center',
    },
    btnGhostText: {
        ...fonts.button,
        color: '#0f172a',
        fontWeight: '600',
        textAlign: 'center',
    },
    content: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
    },
});
